/**
 * PromptStats — 프롬프트 블록 통계 대시보드
 *
 * ai_activity_history raw 실시간 집계 기반.
 * - 상단: StatCard (최근 버킷 총 생성수, 활성 블록 수, 인기 블록 수)
 * - 중단 좌: grain 토글 + 전체 생성 추이 (SVG line chart)
 * - 중단 우: TOP 5 블록 리스트
 * - 하단: 전체 블록 사용량 테이블
 */

import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { toLocalImageUrl } from '@/lib/api'
import { StatCard, Spinner, Badge, Tabs } from '@/ui'
import { TrendingUp, Puzzle, Flame } from 'lucide-react'
import styles from './PromptStats.module.css'

type StatGrain = 'hour' | 'day' | 'week' | 'month'
const VALID_GRAINS: StatGrain[] = ['hour', 'day', 'week', 'month']

interface BucketTotal {
  bucketStart: string
  totalUsage: number
}

interface TopBlock {
  id: string
  slug: string
  nameKo: string
  thumbnailUrl: string | null
  weeklyUsageCount: number
  totalUsageCount: number
  popularityRank: number | null
}

interface AllBlockSummary {
  id: string
  slug: string
  nameKo: string
  weeklyUsageCount: number
  totalUsageCount: number
  popularityRank: number | null
}

interface SummaryResponse {
  grain: StatGrain
  latestBucket: string | null
  bucketTotals: BucketTotal[]
  topBlocks: TopBlock[]
  allBlocksWithStats: AllBlockSummary[]
}

const GRAIN_ITEMS = [
  { key: 'hour', label: '시간' },
  { key: 'day', label: '일' },
  { key: 'week', label: '주' },
  { key: 'month', label: '월' },
]

// 서버가 KST 벽시계 문자열("YYYY-MM-DD HH:MM:SS", offset 없음)을 반환하므로
// new Date() timezone 변환 없이 문자열 그대로 파싱한다.
function formatBucket(bucket: string, grain: StatGrain): string {
  const [datePart, timePart = ''] = bucket.split(' ')
  const [, mm, dd] = datePart.split('-')
  if (grain === 'hour') return `${Number(mm)}/${Number(dd)} ${timePart.slice(0, 2)}시`
  if (grain === 'month') return datePart.slice(0, 7).replace('-', '.')
  return `${Number(mm)}/${Number(dd)}` // day, week
}

function BucketTrendChart({ data, grain }: { data: BucketTotal[]; grain: StatGrain }) {
  if (data.length === 0) {
    return <div className={styles.chartEmpty}>집계된 데이터가 없습니다</div>
  }

  const width = 640
  const height = 220
  const padX = 36
  const padY = 28
  const maxVal = Math.max(...data.map((d) => d.totalUsage), 1)
  const stepX = (width - padX * 2) / Math.max(data.length - 1, 1)

  const points = data.map((d, i) => {
    const x = padX + i * stepX
    const y = height - padY - (d.totalUsage / maxVal) * (height - padY * 2)
    return { x, y, bucketStart: d.bucketStart, total: d.totalUsage }
  })

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)},${height - padY} L${points[0].x.toFixed(1)},${height - padY} Z`

  // Y축 눈금 (0, max/2, max)
  const yTicks = [0, maxVal / 2, maxVal]

  return (
    <svg className={styles.chart} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
      {yTicks.map((v) => {
        const y = height - padY - (v / maxVal) * (height - padY * 2)
        return (
          <g key={v}>
            <line x1={padX} y1={y} x2={width - padX} y2={y} className={styles.gridLine} />
            <text x={padX - 6} y={y + 4} className={styles.axisLabel} textAnchor="end">{Math.round(v)}</text>
          </g>
        )
      })}
      <path d={areaPath} className={styles.chartArea} />
      <path d={linePath} className={styles.chartLine} />
      {points.map((p) => (
        <g key={p.bucketStart}>
          <circle cx={p.x} cy={p.y} r={3.5} className={styles.chartDot} />
          <text x={p.x} y={height - padY + 16} className={styles.axisLabel} textAnchor="middle">
            {formatBucket(p.bucketStart, grain)}
          </text>
          <title>{`${p.bucketStart} · ${p.total}회`}</title>
        </g>
      ))}
    </svg>
  )
}

export function PromptStats() {
  const [searchParams, setSearchParams] = useSearchParams()
  // 잘못된 ?grain= 값은 무시하고 기본 'week'로 폴백 (Tabs 하이라이트 깨짐·잘못된 요청 방지)
  const rawGrain = searchParams.get('grain')
  const grain: StatGrain = VALID_GRAINS.includes(rawGrain as StatGrain) ? (rawGrain as StatGrain) : 'week'
  const setGrain = (g: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('grain', g)
    setSearchParams(next, { replace: true })
  }

  const { data, error, isLoading } = useApiFetch<SummaryResponse>(
    `/api/admin/prompt-stats/summary?topN=5&weeks=12&grain=${grain}`,
  )

  if (isLoading) {
    return <div className={styles.loadingRow}><Spinner /></div>
  }
  if (error || !data) {
    return <div className={styles.errorBox}>통계 조회 실패: {error ?? '알 수 없는 오류'}</div>
  }

  const latestBucketTotal = data.bucketTotals.length > 0
    ? data.bucketTotals[data.bucketTotals.length - 1].totalUsage
    : 0
  const activeBlockCount = data.allBlocksWithStats.length
  const rankedBlockCount = data.allBlocksWithStats.filter((b) => b.popularityRank !== null).length

  return (
    <div className={styles.root}>
      <div className={styles.statsRow}>
        <StatCard
          label={data.latestBucket ? `최근 버킷(${formatBucket(data.latestBucket, data.grain)}) 총 생성수` : '최근 버킷 총 생성수'}
          value={latestBucketTotal.toLocaleString()}
          icon={<TrendingUp size={20} />}
        />
        <StatCard
          label="활성 블록 수"
          value={activeBlockCount}
          icon={<Puzzle size={20} />}
        />
        <StatCard
          label="순위 부여 블록 수"
          value={`${rankedBlockCount} / ${activeBlockCount}`}
          icon={<Flame size={20} />}
        />
      </div>

      <div className={styles.gridRow}>
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>전체 생성 추이</h2>
            <Tabs
              size="sm"
              value={grain}
              onChange={setGrain}
              items={GRAIN_ITEMS}
            />
          </div>
          <BucketTrendChart data={data.bucketTotals} grain={data.grain} />
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>TOP 5 블록 (최근 7일)</h2>
          {data.topBlocks.length === 0 ? (
            <div className={styles.chartEmpty}>순위가 부여된 블록이 없습니다</div>
          ) : (
            <ol className={styles.topList}>
              {data.topBlocks.map((b) => (
                <li key={b.id} className={styles.topItem}>
                  <span className={styles.topRank}>#{b.popularityRank}</span>
                  {b.thumbnailUrl && (
                    <img src={toLocalImageUrl(b.thumbnailUrl)} alt={b.nameKo} className={styles.topThumb} />
                  )}
                  <span className={styles.topName}>{b.nameKo}</span>
                  <Badge color="green">{b.weeklyUsageCount}회</Badge>
                  <span className={styles.topTotal}>누적 {b.totalUsageCount}회</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>전체 블록 사용량</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thRank}>#</th>
              <th>이름</th>
              <th className={styles.thNum}>최근 7일</th>
              <th className={styles.thNum}>누적</th>
            </tr>
          </thead>
          <tbody>
            {data.allBlocksWithStats.map((b) => (
              <tr key={b.id}>
                <td className={styles.tdRank}>
                  {b.popularityRank !== null ? `#${b.popularityRank}` : '–'}
                </td>
                <td>
                  <span className={styles.tdName}>{b.nameKo}</span>
                  <span className={styles.tdSlug}>{b.slug}</span>
                </td>
                <td className={styles.tdNum}>{b.weeklyUsageCount.toLocaleString()}</td>
                <td className={styles.tdNum}>{b.totalUsageCount.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
